import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

function pad2(n: number): string {
  return String(n).padStart(2, "0");
}

/** Định dạng ngày giờ thống nhất dùng ở mọi nơi trong app: "HH:mm dd/MM/yyyy". */
export function formatDate(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  return (
    `${pad2(date.getHours())}:${pad2(date.getMinutes())} ` +
    `${pad2(date.getDate())}/${pad2(date.getMonth() + 1)}/${date.getFullYear()}`
  );
}

/** Same "HH:mm dd/MM/yyyy" format as formatDate — kept as a separate name since most call sites
 * already distinguish "date" vs "date+time" fields semantically, even though the display is
 * identical now. */
export function formatDateTime(value: string | Date): string {
  return formatDate(value);
}

/** "3 ngày trước", "2 tháng trước"... — đơn vị lớn nhất còn >= 1 (tháng ~30 ngày, năm ~365 ngày,
 * đủ chính xác cho mục đích ước lượng nhanh, không cần lịch chính xác tuyệt đối). */
export function formatRelativeTime(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const diffSeconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diffSeconds < 60) return "Vừa xong";

  const units: [number, string][] = [
    [365 * 24 * 3600, "năm"],
    [30 * 24 * 3600, "tháng"],
    [24 * 3600, "ngày"],
    [3600, "giờ"],
    [60, "phút"],
  ];
  for (const [seconds, label] of units) {
    const count = Math.floor(diffSeconds / seconds);
    if (count >= 1) return `${count} ${label} trước`;
  }
  return "Vừa xong";
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" }).format(
    value,
  );
}

/** `value` absent means "hidden by field-visibility", not "actually zero" (vd
 * InventoryDebtOverview.totalStockValue, TransactionProductItem.sellPrice) — render "—" so a
 * hidden amount never looks like a real zero-value record. */
export function formatCurrencyOrDash(value: number | undefined): string {
  return value != null ? formatCurrency(value) : "—";
}

/** Copy text sang clipboard, hoạt động cả trên HTTP thuần (prod chưa có TLS) lẫn khi nút copy
 * nằm trong Dialog/Popover của Radix.
 *
 * 2 cạm bẫy đã gặp:
 * - `navigator.clipboard` chỉ tồn tại/hoạt động trên secure context — gọi thẳng sẽ throw, nên
 *   bọc try/catch và rơi xuống fallback `execCommand("copy")` thay vì fail luôn.
 * - Radix Dialog/Popover có focus trap: một <textarea> tạm gắn vào `document.body` sẽ bị kéo
 *   focus ngược lại ngay lập tức, selection biến mất và `execCommand("copy")` trả về false
 *   (đúng triệu chứng "bấm copy IMEI nhưng không copy được"). Vì vậy gắn textarea NGAY TRONG
 *   container đang giữ focus (dialog/popover) để nó nằm trong vùng trap.
 *
 * Trả về false thay vì throw để caller tự báo lỗi. */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Bỏ qua — thử tiếp bằng execCommand bên dưới.
  }

  const previouslyFocused = document.activeElement as HTMLElement | null;
  const host =
    previouslyFocused?.closest<HTMLElement>(
      "[role='dialog'], [role='alertdialog'], [data-radix-popper-content-wrapper]",
    ) ?? document.body;

  const el = document.createElement("textarea");
  el.value = text;
  // readOnly: chặn bàn phím ảo bật lên trên mobile; contentEditable + kích thước 1px là mẹo để
  // iOS Safari chịu select() nội dung của một input ẩn.
  el.readOnly = true;
  el.contentEditable = "true";
  el.setAttribute("aria-hidden", "true");
  el.style.cssText =
    "position:fixed;top:0;left:0;width:1px;height:1px;padding:0;border:none;outline:none;box-shadow:none;background:transparent;opacity:0;";
  host.appendChild(el);

  try {
    el.focus({ preventScroll: true });
    el.select();
    el.setSelectionRange(0, text.length);
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    el.remove();
    previouslyFocused?.focus?.();
  }
}

export function getInitials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}
